import styled, { css } from 'styled-components';
import ThemedText from './components/ThemedText';
import { DATA_GRABS, SELECTABLE_ITEM_COLOR } from './design';

export const SPACING = 4;
export const sp = (n: number) => `${SPACING * n}px`;

// The "Dividers & Borders" color option feeds this from a single custom property
// on the root (see OrderConfirmationContent); the fallback covers the loading
// skeleton, which renders outside that root.
const DIVIDERS_COLOR = `var(--ocw-border-color, ${SELECTABLE_ITEM_COLOR})`;
const PRIMARY_TEXT_COLOR = `var(--ocw-primary-text-color, ${SELECTABLE_ITEM_COLOR})`;
const SECONDARY_TEXT_COLOR = `var(--ocw-secondary-text-color, ${SELECTABLE_ITEM_COLOR})`;

export const COLORS = {
    border: DIVIDERS_COLOR,
    // Icons follow the primary paragraph text color, not the borders option.
    icon: PRIMARY_TEXT_COLOR,
    // Text-role colors for the few places ThemedText cannot render: a form
    // control, an HTML blob, and a swatch that has its own error state. The
    // provider sets these alongside the per-element rules.
    primaryText: PRIMARY_TEXT_COLOR,
    secondaryText: SECONDARY_TEXT_COLOR,
    // The customization pill's surface. Mixing the borders color toward
    // `transparent` rather than white lets the page background through, so the
    // wash lands lighter than the border on a light site and darker on a dark
    // one -- whichever direction the themed text needs to stay readable.
    customizationSurface: `color-mix(in srgb, ${DIVIDERS_COLOR} 12%, transparent)`,
    // Outline for the price-modifier chip inside that pill: a wash of the pill
    // could not separate the two without darkening the chip's own text.
    priceModifierBorder: `color-mix(in srgb, ${DIVIDERS_COLOR} 40%, transparent)`,
    // Skeleton bars and disabled-tag fills: a surface, not a border, so the
    // "Dividers & Borders" color must not reach them.
    mutedFill: '#E5E7EB',
    // Focus affordance. Deliberately outside the themed palette: it has to stay
    // distinguishable from whatever border color the user picked.
    focusBorder: '#000000',
    bgAlt: '#F3F4F6',
    pillInfoBg: '#E0F2FE',
    pillInfoFg: '#075985',
    pillInfoBorder: '#7DD3FC',
    pillSuccessBg: '#DCFCE7',
    pillSuccessFg: '#166534',
    pillSuccessBorder: '#86EFAC',
    pillNeutralBg: '#F3F4F6',
    pillNeutralFg: '#374151',
    pillNeutralBorder: '#D1D5DB',
    pillErrorBg: '#FEE2E2',
    pillErrorFg: '#991B1B',
    pillErrorBorder: '#FCA5A5',
    pillWarningBg: '#FEF3C7',
    pillWarningFg: '#92400E',
    pillWarningBorder: '#FCD34D',
    noticeWarningBg: '#FEF3C7',
    noticeWarningFg: '#92400E',
    noticeWarningBorder: '#F59E0B',
    noticeInfoBg: '#E0F2FE',
    noticeInfoFg: '#075985',
    noticeInfoBorder: '#7DD3FC',
    noticeErrorBg: '#FEE2E2',
    noticeErrorFg: '#991B1B',
    noticeErrorBorder: '#EF4444',
    // Mirrors the snipcart-client CoreNotice tokens (accented notices).
    accentNoticeFg: 'hsl(0deg 0% 19%)',
    accentNoticeBorder: 'hsl(240deg 4% 90%)',
    accentNoticeSuccessBg: 'hsl(153deg 91% 95%)',
    accentNoticeSuccessAccent: 'hsl(153deg 51% 44%)',
    accentNoticeErrorBg: 'hsl(0deg 91% 95%)',
    accentNoticeErrorAccent: 'hsl(0deg 77% 49%)',
    accentNoticeWarningBg: 'hsl(0deg 0% 100%)',
    accentNoticeWarningAccent: 'hsl(47deg 100% 50%)',
    // Mirrors the snipcart-client `buttonDanger` tokens.
    dangerFg: 'hsl(0deg 77% 49%)',
    dangerBg: 'hsl(0deg 91% 95%)',
    dangerHoverFg: 'hsl(0deg 100% 100%)',
    dangerHoverBg: 'hsl(0deg 77% 49%)',
};

// Layout only, like every themed text block below. Typography belongs to the
// picked site-theme text style alone: a declaration here would reach the
// theme's default style (which no theme rule targets) but lose to its heading
// and paragraph rules, so one text role would render at two different sizes
// depending on the style picked.
const sectionTitleLayout = css`
    margin: 0 0 ${sp(4.5)} 0;
    text-align: start;
`;

/** Subsection titles: order summary, contact, shipping/pickup, payment. */
export const OrderViewSectionTitle = styled(ThemedText).attrs({
    textRole: 'detailsTitle' as const,
    headingLevel: 3 as const,
})`
    ${sectionTitleLayout}
`;

/**
 * The order summary title when the intro header is hidden — it is then the
 * widget's first and only top-level title.
 */
export const OrderViewPrimarySectionTitle = styled(ThemedText).attrs({
    textRole: 'primaryTitle' as const,
    headingLevel: 2 as const,
})`
    ${sectionTitleLayout}
`;

// Margins are forced because the header's 18px and 36px gaps have to be exact:
// a site theme's heading rules are two-ID selectors a plain margin would lose to.
export const OrderViewTitle = styled(ThemedText).attrs({
    textRole: 'primaryTitle' as const,
    headingLevel: 2 as const,
})`
    margin: 0 !important;
    text-align: center;
`;

// No max-width on purpose: the widget fills whatever width it is given and
// sizing is left to the user's own layout.
export const OrderViewContent = styled.div`
    padding-block-end: ${sp(10)};
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${sp(9)};
    margin: 0;
    color: ${COLORS.primaryText};
    /* Pinned so a site theme's alignment can't leak in by inheritance: a direct
     * rule beats an inherited value and start cascades to descendants (the few
     * that differ — header, line-item price — override it). */
    text-align: start;
`;

export const OrderViewHeader = styled.header`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${sp(9)};
`;

export const OrderViewHeaderIntro = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(4.5)};
`;

// Sits under the primary title in the intro header but reads as a subsection
// heading, so it follows the details-title design settings. Margin forced for
// the same reason as OrderViewTitle.
export const OrderViewSubtitle = styled(ThemedText).attrs({
    textRole: 'detailsTitle' as const,
})`
    margin: 0 !important;
`;

export const OrderViewInvoiceNumber = styled.div`
    display: flex;
    gap: 0.3em;
`;

export const OrderViewInvoiceNumberLabel = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})``;

export const OrderViewInvoiceNumberValue = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
    defaultTag: 'span' as const,
})``;

export const OrderViewColumns = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${sp(9)};

    & > * {
        flex: 1 1 0;
        min-width: 0;
    }

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

export const OrderViewSection = styled.section`
    display: flex;
    flex-direction: column;
`;

export const OrderViewLineItems = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(3)};
    margin-block-end: ${sp(4.5)};
`;

export const OrderViewDataCardStack = styled.div`
    display: flex;
    flex-direction: column;

    && > * {
        border-radius: 0;
        margin-block-start: -1px;
    }

    && > *:first-child {
        margin-block-start: 0;
        border-start-start-radius: 5px;
        border-start-end-radius: 5px;
    }

    && > *:last-child {
        border-end-start-radius: 5px;
        border-end-end-radius: 5px;
    }
`;

export const OrderViewPaymentInstructions = styled.div`
    color: ${COLORS.primaryText};
    background-color: ${COLORS.bgAlt};
    padding: ${sp(1.5)} ${sp(2)};
    border-radius: 6px;
    margin-block-start: ${sp(0.5)};

    p {
        margin: 0 0 ${sp(1)} 0;
    }
    p:last-child {
        margin-bottom: 0;
    }
    a {
        color: inherit;
    }
`;

export const OrderViewPaymentNoticeWrapper = styled.div`
    margin-block-end: ${sp(2)};
`;

export const OrderViewCancellation = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(2)};
`;

export const OrderViewCancellationBox = styled.div.attrs({
    'data-grab': DATA_GRABS.dividers,
})`
    display: flex;
    flex-direction: column;
    gap: ${sp(3)};
    padding: ${sp(3)};
    border: 1px solid ${COLORS.border};
    border-radius: 6px;
    background-color: ${COLORS.bgAlt};
`;

export const OrderViewCancellationText = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
})``;

export const OrderViewCancellationActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${sp(3)};
`;

export const OrderViewLinkButton = styled.button`
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-align: start;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        text-decoration: none;
    }

    &:disabled {
        cursor: default;
    }
`;

export const OrderViewDangerButton = styled.button`
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${sp(1.5)};
    align-self: flex-start;
    padding: ${sp(2.5)} ${sp(4)};
    border: none;
    border-radius: 6px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    color: ${COLORS.dangerFg};
    background-color: ${COLORS.dangerBg};
    cursor: pointer;

    &:hover:not(:disabled) {
        color: ${COLORS.dangerHoverFg};
        background-color: ${COLORS.dangerHoverBg};
    }

    &:disabled {
        cursor: default;
    }
`;

export const OrderViewCancellationField = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(1)};
`;

export const OrderViewCancellationFieldHeader = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
})`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${sp(2)};
`;

// Not a ThemedText: the at-the-limit error color has to win over the role's.
export const OrderViewCancellationCounter = styled.span<{ $isFull: boolean }>`
    color: ${({ $isFull }) =>
        $isFull ? COLORS.dangerFg : COLORS.secondaryText};
`;

export const OrderViewCancellationTextarea = styled.textarea`
    font: inherit;
    color: ${COLORS.primaryText};
    padding: ${sp(2)};
    border: 1px solid ${COLORS.border};
    border-radius: 6px;
    background-color: #ffffff;
    resize: vertical;

    /* The browser outline is kept as a guaranteed indicator: the border color
     * alone is user-configurable and could match focusBorder. */
    &:focus {
        border-color: ${COLORS.focusBorder};
    }
`;
